import React from 'react';
import { createRoot, type Root } from 'react-dom/client';

import { getMountedComponent, COMPONENT_MAP } from '@/App';
import '@/styles/app.css';

const mountedNodes = document.querySelectorAll<HTMLElement>('.gscs-react-root');
const roots = new WeakMap<HTMLElement, Root>();

function renderMountedNode(node: HTMLElement) {
	const mountedComponent = getMountedComponent(node);

	if (!mountedComponent) {
		return;
	}

	const { Component, props } = mountedComponent;
	const root = roots.get(node) ?? createRoot(node);

	if (!roots.has(node)) {
		roots.set(node, root);
	}

	node.dataset.reactMounted = 'true';

	root.render(
		<React.StrictMode>
			<Component {...props} />
		</React.StrictMode>
	);
}

function mountAllRoots(scope: ParentNode = document) {
	scope.querySelectorAll<HTMLElement>('.gscs-react-root').forEach((node) => {
		renderMountedNode(node);
	});
}

mountedNodes.forEach((node) => {
	renderMountedNode(node);
});

if (typeof MutationObserver !== 'undefined') {
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				mutation.addedNodes.forEach((addedNode) => {
					if (!(addedNode instanceof HTMLElement)) {
						return;
					}

					if (addedNode.matches('.gscs-react-root')) {
						renderMountedNode(addedNode);
						return;
					}

					mountAllRoots(addedNode);
				});
			}

			if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.target.matches('.gscs-react-root')) {
				renderMountedNode(mutation.target);
			}
		});
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['data-component', 'data-props'],
	});
}

if (mountedNodes.length === 0) {
	const previewRoot = document.querySelector<HTMLElement>('#app');

	if (previewRoot) {
		const HeroShowcase = COMPONENT_MAP['hero-showcase'];
		const root = createRoot(previewRoot);

		root.render(
			<React.StrictMode>
				<div className="min-h-screen bg-background px-6 py-10 md:px-10 md:py-16 antialiased flex items-center justify-center flex-col">
					<div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center gap-8">
						<div className="max-w-3xl">
							<p className="text-sm font-semibold uppercase tracking-[0.3em] text-accent">Vite Preview</p>
							<h1 className="mt-4 font-serif text-4xl tracking-tight text-foreground md:text-6xl">Local component preview</h1>
							<p className="mt-4 text-base leading-8 text-muted-foreground md:text-lg">
								This page exists only for front-end development on localhost. WordPress templates still render the real site layout.
							</p>
						</div>
						{HeroShowcase && (
							<HeroShowcase
								title="GSCS Theme Preview"
								description="Use this Vite page to live-preview React and Tailwind changes. Open your WordPress local URL to verify the same components inside the theme."
								primaryCta={{ label: 'Open WordPress site', url: '#' }}
								secondaryCta={{ label: 'Inspect components', url: '#' }}
							/>
						)}
					</div>
				</div>
			</React.StrictMode>
		);
	}
}
